import { useEffect, useContext } from "react";
import { AuthContext } from "../../context/user.context";
import { getLoansByDealership } from "../../utils/core";

const STORAGE_KEY = "loanStartNotified";

const loadNotified = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

const saveNotified = (ids) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // almacenamiento no disponible: el dedupe solo vale para la sesión
  }
};

/**
 * Notificaciones de escritorio para préstamos firmados cuyo horario de inicio
 * ya pasó y nadie los inició. Corre mientras la app de concesionaria está
 * abierta (aunque la pestaña esté en segundo plano) y avisa por cada préstamo
 * una sola vez (dedupe en localStorage).
 */
export const useLoanStartNotifications = () => {
  const { currentUser } = useContext(AuthContext);
  const permissionsKey = (currentUser?.permissions || []).join(",");

  useEffect(() => {
    if (!("Notification" in window)) return undefined;
    if (Notification.permission !== "granted") return undefined;

    const dealershipId = currentUser?.dealershipId || currentUser?.id;
    const operatingUnitId = currentUser?.activeUnitId || null;
    const hasLoanPermissions =
      permissionsKey.includes("loans.view_unit") ||
      permissionsKey.includes("loans.view_own");
    if (!hasLoanPermissions || !dealershipId) return undefined;

    let cancelled = false;
    let notified = loadNotified();
    let timeoutId = null;

    const check = async () => {
      try {
        const res = await getLoansByDealership({
          dealershipId,
          operatingUnitId,
          status: "signed",
        });
        if (cancelled) return;
        const now = Date.now();
        const due = (res.data || []).filter(
          (loan) => loan.startAt && new Date(loan.startAt).getTime() <= now,
        );
        const newly = due.filter((loan) => !notified.includes(loan.id));

        for (const loan of newly) {
          const car = loan.car
            ? `${loan.car.brand} ${loan.car.model} ${loan.car.year}`.trim()
            : "Vehículo";
          const when = new Date(loan.startAt).toLocaleString("es-AR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
          new Notification("Préstamo por iniciar", {
            body: `${car} · ${loan.customerName || "Cliente"} — debía iniciarse ${when}. Inicialo desde Préstamos.`,
            tag: `loan-start-${loan.id}`,
          });
        }

        if (newly.length) {
          notified = [...new Set([...notified, ...newly.map((l) => l.id)])];
          saveNotified(notified);
        }
      } catch {
        // sin conexión o error transitorio: se reintenta en el próximo ciclo
      }
    };

    check();
    // Como los préstamos se toman en franjas de 30', el chequeo se alinea a
    // las horas en punto y medias horas (20:00, 20:30, 21:00…).
    const runAligned = () => {
      const now = new Date();
      const next = new Date(now);
      next.setMinutes(now.getMinutes() < 30 ? 30 : 60, 0, 0, 0);
      timeoutId = setTimeout(() => {
        check();
        runAligned();
      }, Math.max(1000, next.getTime() - now.getTime()));
    };
    runAligned();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    currentUser?.dealershipId,
    currentUser?.id,
    currentUser?.activeUnitId,
    permissionsKey,
  ]);
};

export default useLoanStartNotifications;